import {
  AnswerSelectDto,
  QuestionType,
  SurveySnapshotChapterIdentifier,
  SurveySnapshotSelectDto,
} from '@@types/api.types';

const CHAPTER_LABELS: Record<SurveySnapshotChapterIdentifier, string> = {
  [SurveySnapshotChapterIdentifier.BASELINE_QUESTIONS]: 'TRL Baseline',
  [SurveySnapshotChapterIdentifier.MAIN_QUESTIONS]: 'Research & Development',
  [SurveySnapshotChapterIdentifier.USE_QUESTIONS]: 'Use Questions',
};

/**
 * Gets the display label for a chapter based on its identifier.
 * Falls back to the chapter name if identifier is not found.
 */
export function getChapterLabel(params: { identifier?: SurveySnapshotChapterIdentifier; name: string }): string {
  const { identifier, name } = params;

  if (identifier && CHAPTER_LABELS[identifier]) {
    return CHAPTER_LABELS[identifier];
  }

  return name;
}

/**
 * Gets the index of a question within a survey snapshot.
 *
 * @param params - The parameters object
 * @param params.questionId - The ID of the question to find
 * @param params.sectionId - The ID of the section containing the question
 * @param params.snapshot - The survey snapshot containing chapters, sections, and questions
 * @returns The zero-based index of the question in the flattened survey structure, or 0 if the question is not found or required parameters are missing
 */
export const getQuestionIndexInSurvey = (params: {
  questionId?: string;
  sectionId?: string;
  snapshot?: SurveySnapshotSelectDto;
}) => {
  const { questionId, sectionId, snapshot } = params;

  if (!questionId || !sectionId || !snapshot) {
    return 0;
  }

  const flatQuestions = snapshot.chapters
    .flatMap((chapter) => chapter.sections)
    .flatMap((section) =>
      section.questions.map((questionId) => ({
        sectionId: section.id,
        questionId,
      })),
    );

  const questionIndex = flatQuestions.findIndex(
    (question) => question.questionId === questionId && question.sectionId === sectionId,
  );

  return Math.max(questionIndex, 0);
};

export const isQuestionAnswered = (params: {
  answers?: AnswerSelectDto[];
  questionId?: string;
  questionType?: QuestionType;
  sectionId?: string;
}): boolean => {
  const { answers, questionId, questionType, sectionId } = params;

  if (!answers || !questionId || !sectionId) {
    return false;
  }

  const answer = answers.find((item) => item.questionId === questionId && item.sectionId === sectionId);
  if (!answer) return false;

  if (typeof answer.response === 'string' && answer.response.trim() !== '') {
    return true;
  }

  if (questionType === QuestionType.TRL_CHARACTERISTICS) {
    const hasDetails = Boolean(answer.details && answer.details.trim() !== '');
    const additionalData = answer.additionalData;

    if (!additionalData) {
      return false;
    }

    const hasCostEstimate = additionalData.costEstimate !== undefined && additionalData.costEstimate !== null;
    const hasTimeSpent = additionalData.timeSpent !== undefined && additionalData.timeSpent !== null;
    const hasTimeUnit = Boolean(additionalData.timeUnit);

    return Boolean(hasDetails && hasCostEstimate && hasTimeSpent && hasTimeUnit);
  }

  return false;
};
